/**
 * VM Create/Modify Forms
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { escapeAttr, escapeHtml, formatBytes } from '../utils.js';
import { vms, setVms, getVmById } from '../globals.js';
import { declareLoading, declareLoaded, notificationDialog, confirmDialog } from '../ui/dialogs.js';
import { generateComboSelect, generateToggle, generateToggleList, getToggleListSelected } from '../ui/forms.js';
import { openDrivesDialog, getCurrentDrives, wereDrivesModified, resetDriveState } from './drives.js';
import { renderVmTable } from './table.js';
import { openCpuOptionsDialog, getCurrentCpuConfig, resetCpuState, setCpuConfig } from './cpu.js';
import type { CpuConfig } from './cpu.js';

// Module state for enlightenments
let currentEnlightenments: string[] | null = null;

// Module state for services
let currentServices: string[] | null = null;

// Module state for PCI passthrough devices
let currentPciDevices: string[] = [];
let pciDevicesModified = false;

// --- VM Template System ---

import type { VmTemplate } from '../api/types.js';

function collectCurrentTemplate(name: string): VmTemplate {
  const selectedBridges: string[] = [];
  $('.bridge-toggle').each(function () {
    const toggleId = $(this).data('toggle-id') as string;
    if ($('#' + toggleId).is(':checked')) {
      selectedBridges.push($(this).val() as string);
    }
  });

  const cpuConfig = getCurrentCpuConfig();

  return {
    name,
    cpus: parseInt($('#cpus').val() as string) || 2,
    auto_pin_cpus: $('#auto_pin_cpus').is(':checked'),
    dedicated: $('#dedicated').is(':checked'),
    hugepages: ($('#hugepages').val() as string) || 'auto',
    mem_gb: parseFloat($('#mem').val() as string) || 4,
    balloon: $('#balloon').is(':checked'),
    display_drv: ($('#display_drv').val() as string) || null,
    bridges: selectedBridges,
    pass_vlans_tagged: $('#pass_vlans_tagged').is(':checked'),
    kvm: $('#kvm').is(':checked'),
    uefi: $('#uefi').is(':checked'),
    swtpm: $('#swtpm').is(':checked'),
    mskeys: $('#mskeys').is(':checked'),
    enabled: $('#enabled').is(':checked'),
    auto_start: $('#auto_start').is(':checked'),
    keep_running: $('#keep_running').is(':checked'),
    rdp_username: ($('#rdp_username').val() as string) || '',
    rdp_local_admin: $('#rdp_local_admin').is(':checked'),
    hv_enlightenments: currentEnlightenments ?? [],
    services: currentServices ?? [],
    cpu_model: cpuConfig?.model ?? null,
    cpu_flags: cpuConfig?.flags ?? [],
    threads_per_cpu: cpuConfig?.threadsPerCpu ?? 1,
    base_image_pool: (() => {
      if (!$('#boot_drive_source').length) return null;
      if ($('#boot_drive_source').val() !== 'base') return null;
      const val = $('#boot_drive_base').val() as string;
      if (!val) return null;
      return val.substring(0, val.indexOf('/'));
    })(),
    base_image: (() => {
      if (!$('#boot_drive_source').length) return null;
      if ($('#boot_drive_source').val() !== 'base') return null;
      const val = $('#boot_drive_base').val() as string;
      if (!val) return null;
      return val.substring(val.indexOf('/') + 1);
    })(),
  };
}

function applyTemplate(template: VmTemplate): void {
  $('#cpus').val(template.cpus);
  $('#auto_pin_cpus').prop('checked', template.auto_pin_cpus);
  if (template.dedicated !== undefined) $('#dedicated').prop('checked', template.dedicated);
  if (template.hugepages) $('#hugepages').val(template.hugepages);
  $('#mem').val(template.mem_gb);
  $('#balloon').prop('checked', template.balloon);
  if (template.display_drv) $('#display_drv').val(template.display_drv);
  $('#pass_vlans_tagged').prop('checked', template.pass_vlans_tagged);
  $('#kvm').prop('checked', template.kvm);
  $('#uefi').prop('checked', template.uefi);
  $('#swtpm').prop('checked', template.swtpm);
  $('#mskeys').prop('checked', template.mskeys);
  $('#enabled').prop('checked', template.enabled);
  $('#auto_start').prop('checked', template.auto_start);
  $('#keep_running').prop('checked', template.keep_running);
  $('#rdp_username').val(template.rdp_username);
  $('#rdp_local_admin').prop('checked', template.rdp_local_admin);

  // Apply bridge toggles — skip bridges not present in the current DOM
  const bridgeSet = new Set(template.attachments || template.bridges || []);
  $('.bridge-toggle').each(function () {
    const toggleId = $(this).data('toggle-id') as string;
    const bridgeName = $(this).val() as string;
    $('#' + toggleId).prop('checked', bridgeSet.has(bridgeName));
  });

  // Apply sub-dialog module state
  currentEnlightenments = template.hv_enlightenments.length > 0 ? template.hv_enlightenments : null;
  currentServices = template.services.length > 0 ? template.services : null;

  // Apply CPU config if present
  if (template.cpu_model) {
    const cpuCfg: CpuConfig = {
      model: template.cpu_model,
      flags: template.cpu_flags ?? [],
      threadsPerCpu: template.threads_per_cpu ?? 1,
    };
    setCpuConfig(cpuCfg);
  }

  // Apply base image selection (create mode only)
  if (template.base_image_pool && template.base_image && $('#boot_drive_source').length) {
    $('#create_boot_drive').prop('checked', true);
    $('#boot_drive_source').val('base');
    const targetVal = `${template.base_image_pool}/${template.base_image}`;
    if ($(`#boot_drive_base option[value="${targetVal}"]`).length) {
      $('#boot_drive_base').val(targetVal);
    }
    $('#boot_drive_source').trigger('change');
  }
}

/**
 * Open enlightenments configuration dialog
 */
export async function vmModifyEnlightenments(vmId: number | null = null): Promise<void> {
  const title = 'Enabled Hyper-V Enlightenments';
  const [enlightenments, enlightenmentSets] = await Promise.all([
    apiCall(api.getHvEnlightenments(), 'Failed to Load Enlightenments'),
    apiCall(api.getHvEnlightenmentSets(), 'Failed to Load Enlightenment Sets'),
  ]);
  if (!enlightenments || !enlightenmentSets) return;

  let selected = currentEnlightenments;
  if (selected === null) {
    if (vmId !== null) {
      const vm = getVmById(vmId);
      if (vm) {
        const vmData = vm as unknown as Record<string, unknown>;
        if (vmData.hv_enlightenments) {
          selected = vmData.hv_enlightenments as string[];
        } else if (vmData.hv_compat === true) {
          selected = enlightenmentSets.compatible || [];
        } else {
          selected = enlightenmentSets.recommended || [];
        }
      } else {
        selected = enlightenmentSets.recommended || [];
      }
    } else {
      selected = enlightenmentSets.recommended || [];
    }
  }

  const enlightenmentKeys = Object.keys(enlightenments);
  let form = '<span id="enlightenment_area">';
  form += generateToggleList('hv_enlightenments', enlightenmentKeys, selected, enlightenments);
  form += '</span><br>';

  const enlightenmentSetOptions: Record<string, string> = {
    custom: 'Custom Selection',
  };
  for (const set of Object.keys(enlightenmentSets)) {
    enlightenmentSetOptions[set] = set;
  }
  form += generateComboSelect('hv_enlightenment_set', 'custom', enlightenmentSetOptions);

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Set VM Hyper-V Enlightenments</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: title,
      zIndex: 10000,
      autoOpen: true,
      width: 'auto',
      resizable: false,
      buttons: {
        OK: function () {
          if ($('#hv_enlightenments_hv_passthrough').is(':checked')) {
            currentEnlightenments = ['hv_passthrough'];
          } else {
            currentEnlightenments = getToggleListSelected('hv_enlightenments');
          }
          $(this).dialog('close');
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
      open: function () {
        function updatePassthroughVisibility() {
          const passthrough = $('#hv_enlightenments_hv_passthrough').is(':checked');
          $('.hv_enlightenments-toggle').each(function () {
            if ($(this).val() !== 'hv_passthrough') {
              const toggleId = $(this).data('toggle-id') as string;
              const $checkbox = $('#' + toggleId);
              const $formValue = $(this).closest('.form-value');
              // Disable and visually dim the other options when passthrough is enabled
              $checkbox.prop('disabled', passthrough);
              $formValue.toggleClass('setting-readonly', passthrough);
              $formValue.prev('label').toggleClass('setting-readonly', passthrough);
            }
          });
        }
        updatePassthroughVisibility();
        $(document).on('change', '#hv_enlightenments_hv_passthrough', updatePassthroughVisibility);

        $('#hv_enlightenment_set').on('change', function () {
          const selectedSet = $(this).val() as string;
          if (selectedSet === 'custom') {
            $('#enlightenment_area').find("input[type='checkbox']").prop('disabled', false);
          } else {
            $('#enlightenment_area').html(
              generateToggleList(
                'hv_enlightenments',
                enlightenmentKeys,
                enlightenmentSets[selectedSet] || [],
                enlightenments
              )
            );
            $('#enlightenment_area').find("input[type='checkbox']").prop('disabled', true);
          }
          updatePassthroughVisibility();
        });
      },
    });
}

/**
 * Open services configuration dialog
 */
export async function vmModifyServices(vmId: number | null = null): Promise<void> {
  const availableServices = await apiCall(api.getServices(), 'Failed to Load Services');
  if (!availableServices) return;

  let selected = currentServices;
  if (selected === null) {
    if (vmId !== null) {
      const vm = getVmById(vmId);
      if (vm?.services) {
        selected = vm.services;
      } else {
        // Default: all services enabled
        selected = Object.keys(availableServices);
      }
    } else {
      selected = Object.keys(availableServices);
    }
  }

  const serviceKeys = Object.keys(availableServices);
  const form = generateToggleList('vm_services', serviceKeys, selected, availableServices);

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Select which services are available for this VM</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Enabled Services',
      zIndex: 10000,
      autoOpen: true,
      width: 'auto',
      resizable: false,
      buttons: {
        OK: function () {
          currentServices = getToggleListSelected('vm_services');
          $(this).dialog('close');
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Open PCI passthrough devices dialog.
 * Lists available host PCI devices and lets the user pick which to pass through.
 */
async function openPciDevicesDialog(_vmId: number | null, host?: string): Promise<void> {
  const hostDevices = await apiCall(api.getHostPciDevices(host), 'Failed to list PCI devices');
  if (!hostDevices) return;

  // Build table of host devices with checkboxes
  let rows = '';
  for (const dev of hostDevices) {
    // Skip bridges
    if (dev.class && parseInt(dev.class, 16) >> 16 === 0x06) continue;
    const checked = currentPciDevices.includes(dev.bdf) ? 'checked' : '';
    const disabled = dev.warning ? 'disabled' : '';
    const driverBadge = dev.driver
      ? `<span class="badge ${dev.driver === 'vfio-pci' ? 'badge-info' : 'badge-warning'}">${escapeHtml(dev.driver)}</span>`
      : '<span class="badge badge-success">unbound</span>';
    const warningBadge = dev.warning
      ? ` <span class="badge badge-danger" title="${escapeAttr(dev.warning)}">&#9888;</span>`
      : '';
    rows += `<tr${dev.warning ? ' style="opacity:0.5"' : ''}>
      <td><input type="checkbox" class="pci-check" data-bdf="${escapeAttr(dev.bdf)}" ${checked} ${disabled}></td>
      <td>${escapeHtml(dev.bdf)}${warningBadge}</td>
      <td>${escapeHtml(dev.class_name)}</td>
      <td>${escapeHtml(dev.vendor)}:${escapeHtml(dev.device)}</td>
      <td>${driverBadge}</td>
      <td>${dev.iommu_group ? escapeHtml(dev.iommu_group) : '-'}</td>
    </tr>`;
  }

  const html = `<table class="ui-table" style="width:100%">
    <thead><tr><th></th><th>BDF</th><th>Type</th><th>ID</th><th>Driver</th><th>IOMMU</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>`;

  $('<div></div>')
    .appendTo('body')
    .html(html)
    .dialog({
      modal: true,
      title: 'PCI Passthrough Devices',
      width: 700,
      buttons: {
        Save: function () {
          const selected: string[] = [];
          $(this).find('.pci-check:checked').each(function () {
            selected.push($(this).data('bdf') as string);
          });
          currentPciDevices = selected;
          pciDevicesModified = true;
          $(this).dialog('close');
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () { $(this).remove(); },
    });
}

/**
 * Open VM create/modify dialog
 */
export async function vmModifyOrCreate(vmId: number | null = null): Promise<void> {
  let title: string;
  let name: string;
  let description: string;
  let cpus: number;
  let autoPinCpus: boolean;
  let dedicated: boolean;
  let hugepages: string;
  let mem: number;
  let enabled: boolean;
  let displayDrv: string;
  let swtpm: boolean;
  let mskeys: boolean;
  let autoStart: boolean;
  let keepRunning: boolean;
  let kvm: boolean;
  let uefi: boolean;
  let vmBridges: string[] = [];
  let passVlansTagged: boolean = false;
  let vmHost: string | null = null;
  let originalHost: string | null = null;
  let balloon: boolean;
  let macaddr: string = '';
  let rdpUsername: string = '';
  let rdpLocalAdmin: boolean = false;

  // Fetch available bridges, networks, storage pools, disks, and hosts from backend
  const [availableBridges, availableNetworks, initialStoragePools, availableHosts, initialDisks] = await Promise.all([
    apiCall(api.getNetBridges(), 'Failed to Load Bridges').then(r => r ?? []),
    apiCall(api.getNetNetworks(), 'Failed to Load Networks').then(r => r ?? []),
    vmId === null ? api.listStoragePools().catch(() => [] as import('../api/types.js').StoragePool[]) : Promise.resolve([]),
    apiCall(api.getHostsForDropdown(), 'Failed to Load Hosts').then(r => r ?? {}),
    vmId === null ? api.listDisks().catch(() => [] as import('../api/types.js').DiskInfo[]) : Promise.resolve([]),
  ]);
  let storagePools = initialStoragePools;
  let allDisks = initialDisks;

  if (vmId !== null) {
    const vm = getVmById(vmId);
    if (!vm) {
      alert('VM not found');
      return;
    }
    const vmData = vm as unknown as Record<string, unknown>;
    title = 'Modify VM';
    name = vmData.name as string;
    description = vmData.description as string ?? '';
    cpus = vmData.cpus as number;
    autoPinCpus = vmData.auto_pin_cpus as boolean ?? false;
    dedicated = vmData.dedicated as boolean ?? false;
    hugepages = vmData.hugepages as string ?? 'auto';
    mem = vmData.mem_gb as number;
    enabled = vmData.enabled as boolean ?? true;
    displayDrv = vmData.display_drv as string ?? 'std';
    swtpm = vmData.swtpm as boolean ?? true;
    mskeys = vmData.mskeys as boolean ?? true;
    autoStart = vmData.auto_start as boolean ?? true;
    keepRunning = vmData.keep_running as boolean ?? false;
    balloon = vmData.balloon as boolean ?? true;
    kvm = vmData.kvm as boolean ?? true;
    uefi = vmData.uefi as boolean ?? true;
    vmBridges = (vmData.attachments as string[]) ?? (vmData.bridges as string[]) ?? [];
    passVlansTagged = vmData.pass_vlans_tagged as boolean ?? false;
    vmHost = vmData.host as string ?? null;
    originalHost = vmHost;
    macaddr = vmData.macaddr as string ?? '';
    rdpUsername = vmData.rdp_username as string ?? '';
    rdpLocalAdmin = vmData.rdp_local_admin as boolean ?? false;

    // Initialize PCI passthrough devices from existing VM
    const pciData = vmData.pci_devices as Array<{bdf: string}> | undefined;
    currentPciDevices = pciData ? pciData.map(d => d.bdf) : [];
    pciDevicesModified = false;
  } else {
    title = 'Create VM';
    name = 'example_vm';
    description = '';
    cpus = 2;
    autoPinCpus = true;
    dedicated = false;
    hugepages = 'auto';
    mem = 4;
    autoStart = true;
    enabled = true;
    uefi = true;
    displayDrv = 'std';
    swtpm = true;
    mskeys = true;
    keepRunning = false;
    balloon = true;
    kvm = true;
    // Default to a network — the canonical attachment. Prefer the untagged
    // (vlan_id 0) network on the managed bridge if there is one, else the
    // first available network.
    const defaultNet = availableNetworks.find(n => !n.vlan_id) || availableNetworks[0];
    if (defaultNet) {
      vmBridges = [defaultNet.name];
    }
  }

  let form = '<div class="vm-form">';

  // -- Template bar --
  form += `<div class="template-bar">`;
  form += `<label>Template</label>`;
  form += `<select id="template-select"><option value="">(none)</option></select>`;
  form += `<button type="button" class="ui-button" id="btn-template-load">Load</button>`;
  form += `<button type="button" class="ui-button" id="btn-template-save">Save</button>`;
  form += `<button type="button" class="ui-button" id="btn-template-delete">Delete</button>`;
  form += `</div>`;

  // -- General section --
  form += `<div class="form-section">General</div>`;
  form += `<label>Name</label><div class="form-value"><input id='name' name='name' value='${escapeAttr(name)}' pattern='[a-z0-9_]+' title='Lowercase letters, numbers, and underscores only'></div>`;
  form += `<label>Description</label><div class="form-value"><input id='description' name='description' value='${escapeAttr(description)}' placeholder='Human-readable description'></div>`;
  form += `<label>RDP Username</label><div class="form-value"><input id='rdp_username' name='rdp_username' value='${escapeAttr(rdpUsername)}' placeholder='Default username for Remote Desktop'></div>`;
  form += `<label>RDP Local Admin</label><div class="form-value">${generateToggle('rdp_local_admin', rdpLocalAdmin)}</div>`;

  // Host selector (only show if there are remote hosts configured)
  const hostKeys = Object.keys(availableHosts);
  if (hostKeys.length > 1) {
    form += `<label>Host</label><div class="form-value">${generateComboSelect('host', vmHost ?? '', availableHosts)}</div>`;
  }

  // Organization assignment
  const orgsResponse = await apiCall(api.listOrganizations(), 'Failed to Load Organizations');
  if (orgsResponse && orgsResponse.organizations.length > 0) {
    const orgNames = orgsResponse.organizations.map(o => o.name);
    const vmOrgNames = vmId !== null
      ? ((getVmById(vmId) as unknown as Record<string, unknown>)?.organizations as string[] || [])
      : [];
    const orgLabels: Record<string, string> = {};
    for (const o of orgsResponse.organizations) {
      orgLabels[o.name] = o.description || o.name;
    }
    form += generateToggleList('vm_organizations', orgNames, vmOrgNames, orgLabels);
  }

  // -- Resources section --
  form += `<div class="form-section">Resources</div>`;
  form += `<label>CPU Cores</label><div class="form-value"><input type='number' step='1' min='1' id='cpus' name='cpus' value='${cpus}' style='width:80px'></div>`;
  form += `<label>Auto Pin CPUs</label><div class="form-value">${generateToggle('auto_pin_cpus', autoPinCpus)}</div>`;
  form += `<label>Dedicated (low-latency)</label><div class="form-value">${generateToggle('dedicated', dedicated)}</div>`;
  form += `<label>Hugepages</label><div class="form-value">${generateComboSelect('hugepages', hugepages, {
    auto: 'Auto (use when dedicated & pool available)',
    on: 'On (require 2MB hugepages)',
    off: 'Off (never)',
  })}</div>`;
  form += `<label>RAM (GB)</label><div class="form-value"><input type='number' step='0.1' min='1' id='mem' name='mem' value='${mem}' style='width:80px'></div>`;
  form += `<label>Memory Balloon</label><div class="form-value">${generateToggle('balloon', balloon)}</div>`;
  form += `<label>Display Driver</label><div class="form-value">${generateComboSelect('display_drv', displayDrv, {
    std: 'Standard (ramfb/stdvga)',
    virtio: 'VirtIO (virtio_gpu/virtio_vga)',
  })}</div>`;

  // -- Network section --
  // The VM's `networks` field is a flat list of attachment names; the daemon
  // resolves each against [network.X] first, then a literal (external) bridge
  // name. The form presents three categories distinctly:
  //   1. Networks — the canonical attachment (access-port on the network's VLAN)
  //   2. Direct bridge attachment (advanced) — externally_managed bridges only,
  //      for VMs that manage their own L3 (router/firewall appliances) on L2
  //      that vmsmand doesn't control. (The daemon-owned vmsman-br is never a
  //      direct-attach target — attach to a network on it instead.)
  //   3. VLAN trunk mode (advanced) — orthogonal flag that exposes attached
  //      bridges as a trunk port (every admitted VID arrives tagged).
  // Both networks and bridges emit .bridge-toggle so the existing collectors
  // flatten them into the same list the daemon expects.

  const hintStyle = 'grid-column: 1 / -1; color: var(--color-text-muted); font-size: 0.85em; margin: 2px 0 6px;';
  const tagStyle = 'color: var(--color-text-muted); font-size: 0.9em;';

  // (1) Networks — canonical.
  form += `<div class="form-section">Network</div>`;
  if (availableNetworks.length > 0) {
    form += `<div style="${hintStyle}">Pick the network(s) this VM lives on. Most VMs need exactly one — the VM appears as an access port on that VLAN.</div>`;
    for (const net of availableNetworks) {
      const checked = vmBridges.includes(net.name);
      const tags = [
        net.bridge ? `on ${net.bridge}` : '',
        net.vlan_id ? `VLAN ${net.vlan_id}` : '',
        net.cidr ? net.cidr : '',
      ].filter(Boolean);
      const label = escapeHtml(net.name) +
        (tags.length ? ` <span style="${tagStyle}">(${escapeHtml(tags.join(', '))})</span>` : '');
      form += `<label>${label}</label><div class="form-value">${generateToggle('bridge_net_' + net.name, checked)}<input type="hidden" class="bridge-toggle" value="${escapeAttr(net.name)}" data-toggle-id="bridge_net_${escapeAttr(net.name)}"></div>`;
    }
  } else {
    form += `<div style="${hintStyle}">No networks configured. Create one under Network Management, or attach to an external bridge below (advanced).</div>`;
  }

  // (2) Direct bridge attachment — externally_managed bridges only (L2 that
  // vmsmand doesn't create/control). The daemon-owned vmsman-br is never a
  // direct-attach target. A bridge already attached to this VM is kept
  // selectable so legacy configs remain editable.
  const bareBridges: typeof availableBridges = [];
  for (const br of availableBridges) {
    const alreadySelected = vmBridges.includes(br.name);
    if (!br.externally_managed && !alreadySelected) continue;
    bareBridges.push(br);
  }
  if (bareBridges.length > 0) {
    form += `<div class="form-section">Direct bridge attachment (advanced)</div>`;
    form += `<div style="${hintStyle}">Attach directly to an externally-managed L2 bridge. Use only when the VM manages its own L3 — firewalls, routers, or other appliances that do their own addressing.</div>`;
    for (const br of bareBridges) {
      const alreadySelected = vmBridges.includes(br.name);
      const tags = [
        br.vlan_aware ? 'vlan-aware' : '',
        br.external ? 'external' : '',
        br.default ? 'default' : '',
      ].filter(Boolean);
      const label = escapeHtml(br.name) +
        (tags.length ? ` <span style="${tagStyle}">(${escapeHtml(tags.join(', '))})</span>` : '');
      form += `<label>${label}</label><div class="form-value">${generateToggle('bridge_' + br.name, alreadySelected)}<input type="hidden" class="bridge-toggle" value="${escapeAttr(br.name)}" data-toggle-id="bridge_${escapeAttr(br.name)}"></div>`;
    }
  }

  // (3) VLAN trunk mode — orthogonal flag.
  form += `<div class="form-section">VLAN trunk mode (advanced)</div>`;
  form += `<div style="${hintStyle}">When on, the VM receives every VLAN admitted by an attached vlan-aware bridge as a tagged frame. The guest OS must speak 802.1Q itself — useful for router, firewall, and hypervisor VMs that manage their own VLANs. Leave off for normal VMs.</div>`;
  form += `<label>Expose as VLAN trunk</label><div class="form-value">${generateToggle('pass_vlans_tagged', passVlansTagged)}</div>`;

  // MAC + identity.
  form += `<div class="form-section">Identity</div>`;
  form += `<label>MAC Address</label><div class="form-value"><input id='macaddr' name='macaddr' value='${escapeAttr(macaddr)}' placeholder='Leave empty for auto-generated' pattern='([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}' title='Format: XX:XX:XX:XX:XX:XX'></div>`;

  // -- Firmware section --
  form += `<div class="form-section">Firmware &amp; Security</div>`;
  form += `<label>KVM</label><div class="form-value">${generateToggle('kvm', kvm)}</div>`;
  form += `<label>UEFI</label><div class="form-value">${generateToggle('uefi', uefi)}</div>`;
  form += `<label>SWTPM</label><div class="form-value">${generateToggle('swtpm', swtpm)}</div>`;
  form += `<label>Secure Boot</label><div class="form-value">${generateToggle('mskeys', mskeys && uefi)}</div>`;
  if (vmId !== null && uefi) {
    form += `<label></label><div class="form-value"><button class='ui-button' id='btn-clear-uefi-vars'>Reset UEFI Variables</button></div>`;
  }

  // -- Behavior section --
  form += `<div class="form-section">Behavior</div>`;
  form += `<label>Enabled</label><div class="form-value">${generateToggle('enabled', enabled)}</div>`;
  form += `<label>Auto Start</label><div class="form-value">${generateToggle('auto_start', autoStart)}</div>`;
  form += `<label>Keep Running</label><div class="form-value">${generateToggle('keep_running', keepRunning)}</div>`;

  // -- Boot drive (create mode only) --
  function buildPoolOptions(pools: import('../api/types.js').StoragePool[]): string {
    let opts = '';
    for (const pool of pools) {
      const availStr = pool.total_bytes > 0 ? formatBytes(pool.available_bytes) : 'N/A';
      const label = `${escapeHtml(pool.name)} (${escapeHtml(availStr)} free)`;
      opts += `<option value="${escapeAttr(pool.name)}">${label}</option>`;
    }
    return opts;
  }

  function buildBaseImageOptions(disks: import('../api/types.js').DiskInfo[]): string {
    let opts = '<option value="">(none)</option>';
    for (const d of disks) {
      if (!d.is_base || !d.pool) continue;
      const val = `${d.pool}/${d.name}`;
      const sizeStr = d.size_bytes > 0 ? formatBytes(d.size_bytes) : '';
      const encTag = d.encrypted ? ' [Encrypted]' : '';
      const label = `${escapeHtml(d.pool)} / ${escapeHtml(d.name)}${sizeStr ? ' (' + escapeHtml(sizeStr) + ')' : ''}${encTag}`;
      opts += `<option value="${escapeAttr(val)}">${label}</option>`;
    }
    return opts;
  }

  function isSelectedBaseEncrypted(): boolean {
    const baseVal = $('#boot_drive_base').val() as string;
    if (!baseVal) return false;
    const slashIdx = baseVal.indexOf('/');
    const pool = baseVal.substring(0, slashIdx);
    const name = baseVal.substring(slashIdx + 1);
    return allDisks.some(d => d.is_base && d.pool === pool && d.name === name && d.encrypted);
  }

  if (vmId === null) {
    form += `<div class="form-section" id="boot_drive_section">Boot Drive</div>`;
    form += `<label>Create Drive</label><div class="form-value">${generateToggle('create_boot_drive', storagePools.length > 0)}</div>`;
    form += `<label>Source</label><div class="form-value" id="boot_drive_source_row"><select id='boot_drive_source'><option value="blank">New blank disk</option><option value="base">Clone from base image</option></select></div>`;
    form += `<label>Pool</label><div class="form-value" id="boot_drive_options"><select id='boot_drive_pool'>${buildPoolOptions(storagePools)}</select></div>`;
    form += `<label>Size</label><div class="form-value" id="boot_drive_size_row"><input type='number' id='boot_drive_size' value='100' min='1' style='width:80px'> GB</div>`;
    form += `<label>Base Image</label><div class="form-value" id="boot_drive_base_row"><select id='boot_drive_base'>${buildBaseImageOptions(allDisks)}</select></div>`;
    form += `<label>Encrypt</label><div class="form-value" id="boot_drive_encrypt_row">${generateToggle('boot_drive_encrypt', false)}<span id="boot_drive_encrypt_note" style="font-size:0.9em; color:var(--color-text-muted, #666); margin-left:8px"></span></div>`;
    form += `<label>Passphrase</label><div class="form-value" id="boot_drive_passphrase_row"><input type='password' id='boot_drive_passphrase' autocomplete='new-password' style='width:200px'></div>`;
    form += `<label>Confirm</label><div class="form-value" id="boot_drive_passphrase_confirm_row"><input type='password' id='boot_drive_passphrase_confirm' autocomplete='new-password' style='width:200px'></div>`;
  }

  // -- Advanced buttons --
  form += `<div class="form-buttons">`;
  form += `<button class='ui-button' id='btn-enlightenments'>Hyper-V Enlightenments</button>`;
  form += `<button class='ui-button' id='btn-services'>Services</button>`;
  form += `<button class='ui-button' id='btn-drives'>Drives</button>`;
  form += `<button class='ui-button' id='btn-pci-devices'>PCI Passthrough</button>`;
  form += `<button class='ui-button' id='btn-cpu-options'>CPU Options</button>`;
  form += `</div>`;
  form += '</div>';

  $('<div></div>')
    .appendTo('body')
    .html(form)
    .dialog({
      modal: true,
      title: title,
      zIndex: 10000,
      autoOpen: true,
      width: 'auto',
      resizable: false,
      buttons: {
        Submit: async function () {
          const params: Record<string, unknown> = {
            name: $('#name').val(),
            description: $('#description').val(),
            rdp_username: ($('#rdp_username').val() as string).trim() || null,
            rdp_local_admin: $('#rdp_local_admin').is(':checked'),
            cpus: $('#cpus').val(),
            mem_gb: $('#mem').val(),
            uefi: $('#uefi').is(':checked'),
            display_drv: $('#display_drv').val(),
            swtpm: $('#swtpm').is(':checked'),
            mskeys: $('#mskeys').is(':checked'),
            enabled: $('#enabled').is(':checked'),
            auto_start: $('#auto_start').is(':checked'),
            keep_running: $('#keep_running').is(':checked'),
            balloon: $('#balloon').is(':checked'),
            auto_pin_cpus: $('#auto_pin_cpus').is(':checked'),
            dedicated: $('#dedicated').is(':checked'),
            hugepages: ($('#hugepages').val() as string) || 'auto',
            kvm: $('#kvm').is(':checked'),
          };

          // Collect selected bridges from toggles
          const selectedBridges: string[] = [];
          $('.bridge-toggle').each(function () {
            const toggleId = $(this).data('toggle-id') as string;
            if ($('#' + toggleId).is(':checked')) {
              selectedBridges.push($(this).val() as string);
            }
          });
          params.attachments = selectedBridges;
          params.pass_vlans_tagged = $('#pass_vlans_tagged').is(':checked');

          // MAC address: empty string means use auto-generated (send null to clear)
          const macaddrVal = ($('#macaddr').val() as string).trim();
          params.macaddr = macaddrVal || null;

          // Collect host selection (only if host selector was shown)
          if ($('#host').length) {
            const selectedHost = $('#host').val() as string;
            params.host = selectedHost || null;
          }

          // Collect selected organizations
          const selectedOrgs = getToggleListSelected('vm_organizations');
          if (selectedOrgs.length > 0 || (vmId !== null)) {
            params.organizations = selectedOrgs;
          }

          if (currentEnlightenments) {
            params.hv_enlightenments = currentEnlightenments;
          }

          if (currentServices) {
            params.services = currentServices;
          }

          // Only send disks if the user opened and saved the drives dialog
          if (wereDrivesModified()) {
            params.disks = getCurrentDrives();
          }

          // PCI passthrough devices
          if (pciDevicesModified) {
            params.pci_devices = currentPciDevices.map(bdf => ({ bdf }));
          }

          const cpuConfig = getCurrentCpuConfig();
          if (cpuConfig) {
            if (cpuConfig.model) {
              params.cpu_model = cpuConfig.model;
            }
            if (cpuConfig.flags.length > 0) {
              params.cpu_flags = cpuConfig.flags;
            }
            if (cpuConfig.threadsPerCpu) {
              params.threads_per_cpu = cpuConfig.threadsPerCpu;
            }
          }

          // Check for duplicate name (only if name was changed or creating)
          let fail = false;
          const existingVm = vmId !== null ? getVmById(vmId) : null;
          if (params.name !== (existingVm?.name ?? null)) {
            for (const vm of vms) {
              if (vm.name === params.name) {
                alert('A VM by that name is already defined. Pick a different name');
                fail = true;
                break;
              }
            }
          }

          if (!fail) {
            const parent = $(this);
            parent.dialog('disable');
            declareLoading();

            // Create boot drive if requested (new VM only)
            if (vmId === null && $('#create_boot_drive').is(':checked')) {
              const targetHost = ($('#host').val() as string) || undefined;
              const source = ($('#boot_drive_source').val() as string) || 'blank';
              let diskResult: { path: string; format: string; encrypted?: boolean } | null = null;

              if (source === 'base') {
                const baseVal = $('#boot_drive_base').val() as string;
                if (baseVal) {
                  const slashIdx = baseVal.indexOf('/');
                  const basePool = baseVal.substring(0, slashIdx);
                  const baseImage = baseVal.substring(slashIdx + 1);
                  const baseEncrypted = isSelectedBaseEncrypted();
                  const wantEncrypt = !baseEncrypted && $('#boot_drive_encrypt').is(':checked');

                  // Validate passphrase if encrypting
                  if (wantEncrypt) {
                    const pass = ($('#boot_drive_passphrase').val() as string || '').trim();
                    const confirm = ($('#boot_drive_passphrase_confirm').val() as string || '').trim();
                    if (!pass) {
                      alert('Please enter a passphrase for encryption');
                      parent.dialog('enable');
                      declareLoaded();
                      return;
                    }
                    if (pass !== confirm) {
                      alert('Passphrases do not match');
                      parent.dialog('enable');
                      declareLoaded();
                      return;
                    }
                  }

                  const newPassphrase = wantEncrypt ? ($('#boot_drive_passphrase').val() as string || '').trim() : undefined;
                  const cloneResult = await apiCall(
                    api.cloneDisk(basePool, baseImage, params.name as string,
                      basePool, wantEncrypt ? true : undefined, targetHost, undefined,
                      true, undefined, newPassphrase),
                    'Failed to Clone Base Image'
                  );
                  if (cloneResult) {
                    diskResult = { path: cloneResult.path, format: cloneResult.format, encrypted: cloneResult.encrypted || baseEncrypted || wantEncrypt };
                  }
                }
              } else {
                const poolName = $('#boot_drive_pool').val() as string;
                const sizeGb = parseInt($('#boot_drive_size').val() as string) || 100;
                const createResult = await apiCall(api.createDisk({
                  pool: poolName,
                  name: params.name as string,
                  size_gb: sizeGb,
                }, targetHost), 'Failed to Create Boot Drive');
                if (createResult) {
                  diskResult = { path: createResult.path, format: createResult.format, encrypted: createResult.encrypted };
                }
              }

              if (diskResult === null && source !== 'base') {
                // Blank disk creation failed
                declareLoaded();
                parent.dialog('enable');
                return;
              }
              if (diskResult === null && source === 'base' && $('#boot_drive_base').val()) {
                // Base image clone failed
                declareLoaded();
                parent.dialog('enable');
                return;
              }
              if (diskResult) {
                if (!params.disks) {
                  params.disks = [];
                }
                const disk: Record<string, unknown> = { path: diskResult.path, interface: 'virtio', cache: 'writeback', format: diskResult.format };
                if (diskResult.encrypted) disk.encrypted = true;
                (params.disks as Array<Record<string, unknown>>).unshift(disk);
              }
            }

            const apiPromise = vmId !== null
              ? api.updateVm(vmId, params as unknown as import('../api/types.js').VmUpdateRequest)
              : api.createVm(params as unknown as import('../api/types.js').VmCreateRequest);
            const result = await apiCall(apiPromise, 'Failed to Save VM');
            declareLoaded();
            if (result) {
              // Update global VM list with returned data and re-render.
              // Preserve local identity fields — proxied responses from remote
              // hosts include the remote VM's id/remote_id/owner_host, which
              // must not overwrite the local VM's identity.
              if (vmId !== null) {
                const existingStatus = vms.find(vm => vm.id === vmId)?.status;
                const idx = vms.findIndex(vm => vm.id === vmId);
                if (idx !== -1) {
                  const { id: _id, remote_id: _rid, owner_host: _oh, ...safeFields } = result;
                  setVms(vms.map((vm, i) => i === idx ? { ...vm, ...safeFields } : vm));
                }
                const hostChanged = ('host' in params) && originalHost !== (params.host as string | null);
                if (hostChanged) {
                  notificationDialog('Changes saved. The VM host has been changed. Stop the VM and start it again for the change to take effect.');
                } else if (existingStatus === 'running' || existingStatus === 'paused') {
                  notificationDialog('Changes saved. The VM must be powered off and back on for changes to take effect.');
                }
              } else {
                setVms([...vms, { ...result, status: 'stopped' as const }]);
              }
              renderVmTable(vms);
              parent.dialog('close');
            } else {
              parent.dialog('enable');
            }
          }
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
        currentEnlightenments = null;
        currentServices = null;
        currentPciDevices = [];
        pciDevicesModified = false;
        resetDriveState();
        resetCpuState();
      },
      open: function () {
        // --- Template bar handlers ---
        async function refreshTemplateDropdown() {
          const templates = await apiCall(api.listTemplates(), 'Failed to Load Templates');
          const $sel = $('#template-select');
          const prev = $sel.val() as string;
          $sel.empty().append('<option value="">(none)</option>');
          if (templates) {
            for (const t of templates) {
              $sel.append($('<option></option>').val(t.name).text(t.name));
            }
          }
          // Restore previous selection if it still exists
          if (prev && $sel.find(`option[value="${prev}"]`).length) {
            $sel.val(prev);
          }
        }
        refreshTemplateDropdown();

        $('#btn-template-load').on('click', async function () {
          const selected = $('#template-select').val() as string;
          if (!selected) return;
          const template = await apiCall(api.getTemplate(selected), 'Failed to Load Template');
          if (template) {
            applyTemplate(template);
          }
        });

        $('#btn-template-save').on('click', async function () {
          const name = prompt('Template name:');
          if (!name || !name.trim()) return;
          const trimmed = name.trim();
          // Check if template already exists
          const existing = await apiCall(api.listTemplates(), 'Failed to Check Templates');
          if (!existing) return;
          const exists = existing.some(t => t.name === trimmed);
          if (exists) {
            if (!confirm(`Template "${trimmed}" already exists. Overwrite?`)) return;
            await apiCall(api.updateTemplate(trimmed, collectCurrentTemplate(trimmed)), 'Failed to Update Template');
          } else {
            await apiCall(api.createTemplate(collectCurrentTemplate(trimmed)), 'Failed to Save Template');
          }
          await refreshTemplateDropdown();
          $('#template-select').val(trimmed);
        });

        $('#btn-template-delete').on('click', async function () {
          const selected = $('#template-select').val() as string;
          if (!selected) return;
          if (!confirm(`Delete template "${selected}"?`)) return;
          await apiCall(api.deleteTemplate(selected), 'Failed to Delete Template');
          await refreshTemplateDropdown();
        });

        $('#btn-enlightenments').on('click', () => vmModifyEnlightenments(vmId));
        $('#btn-services').on('click', () => vmModifyServices(vmId));
        $('#btn-drives').on('click', () => {
          // Pass the currently selected host to the drives dialog
          const selectedHost = ($('#host').val() as string) || undefined;
          openDrivesDialog(vmId, selectedHost);
        });
        $('#btn-pci-devices').on('click', () => {
          const selectedHost = ($('#host').val() as string) || undefined;
          openPciDevicesDialog(vmId, selectedHost);
        });
        $('#btn-cpu-options').on('click', () => openCpuOptionsDialog(vmId));

        $('#btn-clear-uefi-vars').on('click', () => {
          confirmDialog(
            'This will delete the UEFI NVRAM variables for this VM. All boot entries, Secure Boot keys (if not using the MS template), and other firmware state will be reset. The VM must be stopped. Continue?',
            async () => {
              const result = await apiCall(api.clearUefiVars(vmId!), 'Failed to Clear UEFI Variables');
              if (result) {
                notificationDialog('UEFI variables have been cleared. They will be re-provisioned on next VM start.');
              }
            }
          );
        });

        // Boot drive visibility helper
        function updateBootDriveVisibility() {
          const createDrive = $('#create_boot_drive').is(':checked');
          const source = ($('#boot_drive_source').val() as string) || 'blank';
          const isBlank = source === 'blank';
          const isBase = !isBlank;

          // Source row: visible when create drive is checked
          $('#boot_drive_source_row').prev('label').toggle(createDrive);
          $('#boot_drive_source_row').toggle(createDrive);

          // Pool + Size: visible when create drive AND blank
          $('#boot_drive_options').closest('.form-value').prev('label').toggle(createDrive && isBlank);
          $('#boot_drive_options').closest('.form-value').toggle(createDrive && isBlank);
          $('#boot_drive_size_row').prev('label').toggle(createDrive && isBlank);
          $('#boot_drive_size_row').toggle(createDrive && isBlank);

          // Base image: visible when create drive AND base
          $('#boot_drive_base_row').prev('label').toggle(createDrive && isBase);
          $('#boot_drive_base_row').toggle(createDrive && isBase);

          // Encryption: visible when base source, and base is NOT already encrypted
          const baseEncrypted = isBase && isSelectedBaseEncrypted();
          const showEncrypt = createDrive && isBase && !baseEncrypted;
          $('#boot_drive_encrypt_row').prev('label').toggle(showEncrypt);
          $('#boot_drive_encrypt_row').toggle(showEncrypt);
          // Show note when base is already encrypted
          if (baseEncrypted && createDrive && isBase) {
            $('#boot_drive_encrypt_note').text('Inherited from base image').show();
            $('#boot_drive_encrypt_row').prev('label').show();
            $('#boot_drive_encrypt_row').show();
            $('#boot_drive_encrypt').prop('checked', false).prop('disabled', true);
          } else {
            $('#boot_drive_encrypt_note').text('Full copy required').toggle($('#boot_drive_encrypt').is(':checked'));
            $('#boot_drive_encrypt').prop('disabled', false);
          }

          // Passphrase fields: visible when encrypt is checked and base is not already encrypted
          const showPassphrase = createDrive && isBase && !baseEncrypted && $('#boot_drive_encrypt').is(':checked');
          $('#boot_drive_passphrase_row').prev('label').toggle(showPassphrase);
          $('#boot_drive_passphrase_row').toggle(showPassphrase);
          $('#boot_drive_passphrase_confirm_row').prev('label').toggle(showPassphrase);
          $('#boot_drive_passphrase_confirm_row').toggle(showPassphrase);
        }

        // Refresh storage pools and disks when host changes
        $('#host').on('change', async function () {
          const selectedHost = $(this).val() as string || undefined;
          try {
            storagePools = await api.listStoragePools(selectedHost);
          } catch {
            storagePools = [];
          }
          $('#boot_drive_pool').html(buildPoolOptions(storagePools));
          try {
            allDisks = await api.listDisks(selectedHost);
          } catch {
            allDisks = [];
          }
          $('#boot_drive_base').html(buildBaseImageOptions(allDisks));
        });

        $('#create_boot_drive').on('change', updateBootDriveVisibility);
        $('#boot_drive_source').on('change', updateBootDriveVisibility);
        $('#boot_drive_base').on('change', updateBootDriveVisibility);
        $('#boot_drive_encrypt').on('change', updateBootDriveVisibility);
        updateBootDriveVisibility();
      },
    });
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).vmModifyOrCreate = vmModifyOrCreate;
  (window as unknown as Record<string, unknown>).vmModifyEnlightenments = vmModifyEnlightenments;
  (window as unknown as Record<string, unknown>).vmModify = vmModifyOrCreate;
  (window as unknown as Record<string, unknown>).vmCreate = () => vmModifyOrCreate();
}
